/**
 * Web-app Analytics — minimal activation-funnel event tracker.
 *
 * - Fire-and-forget: never blocks UI, all errors swallowed.
 * - No PII: only an anonymous session ID stored in localStorage.
 * - Uses the existing POST /api/v1/events backend endpoint.
 */

const EVENTS_URL = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'}/api/v1/events`;
const CLIENT_ID_KEY = 'ats_anon_id';

/**
 * Return a persistent anonymous client ID for this browser.
 * Generates a new UUID v4 on first call and stores it in localStorage.
 */
function getClientId(): string {
  try {
    const existing = localStorage.getItem(CLIENT_ID_KEY);
    if (existing) return existing;

    // crypto.randomUUID is available in all modern browsers (Chrome 92+)
    const id = crypto.randomUUID();
    localStorage.setItem(CLIENT_ID_KEY, id);
    return id;
  } catch {
    return 'unknown';
  }
}

/**
 * Send a single event to the backend.
 * Completely fire-and-forget — never throws.
 */
async function sendEvent(eventName: string, properties: Record<string, unknown> = {}): Promise<void> {
  try {
    const clientId = getClientId();

    await fetch(EVENTS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Id': clientId,
      },
      body: JSON.stringify({ event_name: eventName, properties }),
    });
  } catch {
    // Analytics must never crash the app
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Track user_registered — fires once on successful email/OAuth registration.
 * No PII; just counts new accounts.
 */
export function trackUserRegistered(): void {
  void sendEvent('user_registered', {});
}

/**
 * Track cv_generated — fires after a CV is successfully generated.
 */
export function trackCvGenerated(templateId: string): void {
  void sendEvent('cv_generated', { templateId });
}

// ── Payment Funnel Events ────────────────────────────────────────────────────

/**
 * Track upgrade_cta_clicked — user clicked an upgrade/pricing button.
 */
export function trackUpgradeCtaClicked(source: string, properties: Record<string, unknown> = {}): void {
  void sendEvent('upgrade_cta_clicked', { source, ...properties });
}

/**
 * Track checkout_canceled — user returned from Stripe without completing.
 */
export function trackCheckoutCanceled(): void {
  void sendEvent('checkout_canceled', {});
}

// ── Onboarding Funnel Events ─────────────────────────────────────────────────

const ONBOARDING_STEP_NAMES: Record<number, string> = {
  0: 'cv_upload',
  1: 'review',
  2: 'consent',
  3: 'complete',
};

/**
 * Track onboarding_step — fires each time the user reaches a new onboarding step.
 * step 0 = CV Upload, 1 = Review, 2 = Consent, 3 = Complete.
 */
export function trackOnboardingStep(step: number): void {
  void sendEvent('onboarding_step', {
    step,
    stepName: ONBOARDING_STEP_NAMES[step] ?? `step_${step}`,
  });
}
